import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import axios from 'axios';
import { Check, XCircle, Clock } from 'lucide-react';
import { API } from '../utils';

const StatusUpdatePanel = ({ appointment, isQualityControl, onUpdate }) => {
  const { t } = useTranslation();
  const [showStatusForm, setShowStatusForm] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [statusReason, setStatusReason] = useState("");
  const [loading, setLoading] = useState(false);

  const canShowPanel = !isQualityControl &&
    (appointment.status === "scheduled" || appointment.status === "confirmed" || appointment.status === "provisional");

  if (!canShowPanel) return null;

  const handleStatusUpdate = async () => {
    if ((newStatus === "not_completed" || newStatus === "to_reschedule") && !statusReason) {
      toast.error(t('appointment.messages.reasonRequired'));
      return;
    }
    setLoading(true);
    try {
      await axios.patch(`${API}/appointments/${appointment.id}/status`, {
        status: newStatus, status_reason: statusReason || null,
      });
      toast.success(t('appointment.messages.statusUpdated'));
      onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border-t pt-4 mt-4">
      {!showStatusForm ? (
        <button onClick={() => setShowStatusForm(true)}
          className="w-full py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition font-medium"
          data-testid="update-status-btn">
          {t('appointment.updateStatus')}
        </button>
      ) : (
        <div className="space-y-4">
          <div>
            <p className="text-xs text-gray-500 mb-2">Stato Appuntamento:</p>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <button onClick={() => setNewStatus("confirmed")}
                className={`p-3 rounded-lg border-2 flex flex-col items-center gap-1 transition ${
                  newStatus === "confirmed" ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:border-blue-300"
                }`} data-testid="status-confirmed">
                <Check className="text-blue-500" size={24} />
                <span className="text-xs font-medium">Confermato</span>
              </button>
              <button onClick={() => setNewStatus("provisional")}
                className={`p-3 rounded-lg border-2 flex flex-col items-center gap-1 transition ${
                  newStatus === "provisional" ? "border-purple-500 bg-purple-50" : "border-gray-200 hover:border-purple-300"
                }`} data-testid="status-provisional">
                <Clock className="text-purple-500" size={24} />
                <span className="text-xs font-medium">Provvisorio</span>
              </button>
            </div>
          </div>

          <div>
            <p className="text-xs text-gray-500 mb-2">Esito Lavoro:</p>
            <div className="grid grid-cols-3 gap-2">
              <button onClick={() => setNewStatus("completed")}
                className={`p-3 rounded-lg border-2 flex flex-col items-center gap-1 transition ${
                  newStatus === "completed" ? "border-green-500 bg-green-50" : "border-gray-200 hover:border-green-300"
                }`} data-testid="status-completed">
                <Check className="text-green-500" size={24} />
                <span className="text-xs font-medium">{t('appointment.statuses.completed')}</span>
              </button>
              <button onClick={() => setNewStatus("not_completed")}
                className={`p-3 rounded-lg border-2 flex flex-col items-center gap-1 transition ${
                  newStatus === "not_completed" ? "border-red-500 bg-red-50" : "border-gray-200 hover:border-red-300"
                }`} data-testid="status-not-completed">
                <XCircle className="text-red-500" size={24} />
                <span className="text-xs font-medium">{t('appointment.statuses.not_completed')}</span>
              </button>
              <button onClick={() => setNewStatus("to_reschedule")}
                className={`p-3 rounded-lg border-2 flex flex-col items-center gap-1 transition ${
                  newStatus === "to_reschedule" ? "border-yellow-500 bg-yellow-50" : "border-gray-200 hover:border-yellow-300"
                }`} data-testid="status-reschedule">
                <Clock className="text-yellow-500" size={24} />
                <span className="text-xs font-medium">{t('appointment.statuses.to_reschedule')}</span>
              </button>
            </div>
          </div>

          {(newStatus === "not_completed" || newStatus === "to_reschedule") && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('appointment.statusReason')} *</label>
              <textarea value={statusReason} onChange={(e) => setStatusReason(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lojack-red"
                rows={2} required data-testid="status-reason" />
            </div>
          )}

          <div className="flex gap-2">
            <button onClick={() => { setShowStatusForm(false); setNewStatus(""); setStatusReason(""); }}
              className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition">
              {t('common.cancel')}
            </button>
            <button onClick={handleStatusUpdate} disabled={!newStatus || loading}
              className="flex-1 py-2 bg-lojack-red text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="confirm-status-btn">
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : t('common.confirm')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatusUpdatePanel;
